import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { ACCESS_TYPE, PromotionModel, QUOTE_CONTEXTS, REDUCTION_TYPE } from '@bytel/bytel-sales';
import { BasicObject, QuoteContextRenewalModel } from '@bytel/checkout';

import { WallPricesParams } from '@catalog/products/interface/context';

import { ProService } from '@app/base/services/btl-pro.service';
import { CartHelperService } from '@app/checkout/cart/helper/cart-helper.service';
import {
    IPromotionV2,
    IPromotionsCartV2,
    IPromotionsCartV2Produit,
    PromotionsModelWebContainer,
    PromotionsModelWebContainers,
} from '@app/interfaces/api/promotions-v2.interface';
import { PromotionsModelWeb } from '@app/models/promotions.model';
import { Oauth2RessourceService } from '@app/oauth2/oauth2-resources.service';

import { ConfigurationService, Oauth2Service } from '@common';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, forkJoin, from, throwError as observableThrowError, of } from 'rxjs';
import { catchError, map, publishLast, refCount } from 'rxjs/operators';

import { CatalogStorageService } from '../../catalog/catalog-storage.service';
import { PlansRenewDataJson } from '../../catalog/category/plans-renew/plans-renew.inferface';
import {
    ApiContextualiseResponse,
    ApiProduct,
    ApiProductContainers,
    JsonCatalog,
    JsonProduct,
    PlanJsonCategories,
    RenewPlansParams,
} from './products/interface/context';
import { DeliveryModesParams, DeliveryModesResult } from './products/interface/delivery';

export enum CATALOG_TYPE {
    DEFAULT,
    FAI,
}

export enum FUNDING_MODES {
    ALL = 'tous',
    AUTO = 'auto',
    MIN_ONE_OFF = 'min-one-off',
}

/**
 * This class represents the produts Service used to get catalog.
 */
@Injectable({
    providedIn: 'root',
})
export class ProductsServiceBase {
    /**
     * @private: All stored products
     */
    protected products: JsonCatalog = {};
    protected promise: Observable<JsonCatalog>;
    private _scavenger = new Subject();
    private configService: ConfigurationService = inject(ConfigurationService);

    constructor(
        protected http: HttpClient,
        protected catalogStorageService: CatalogStorageService,
        protected oauth2Resource: Oauth2RessourceService,
        protected oauth2: Oauth2Service,
        private proService: ProService,
        protected cartHelperService: CartHelperService,
        private cookieStorage: CookieService,
    ) {}

    public ngOnDestroy() {
        // Used for scavenger subscription removal
        this._scavenger.next(null);
        this._scavenger.complete();
    }
    /**
     *
     * @example
     * ProductsService.getProducts()
     *
     */
    public getProducts(): Observable<JsonCatalog> {
        if (this.products) {
            this.promise = of(this.products);
            return this.promise;
        }

        const sproducts: JsonCatalog = this.catalogStorageService.getItem(this.catalogStorageService.KEY.PRODUCTS);

        if (sproducts) {
            this.products = sproducts;
            this.promise = from([sproducts]);
            return this.promise;
        }

        if (!this.promise) {
            // Start stock request
            this.promise = this.oauth2Resource
                .ventes()
                .produits()
                .get()
                .pipe(
                    catchError((err) => this.handleError(err)),
                    map((res) => {
                        this.products = res;
                        this.products.EPD0000000 = {
                            type_id: 'edp',
                            gencode: 'EPD0000000',
                            sku: 'EPD0000000',
                            price: 9999,
                            products_ids: null,
                            name: 'Etalement de paiement',
                            media: null,
                        }; // @todo : is for mock ...
                        return this.products;
                    }),
                    publishLast(),
                    refCount(),
                );
        }

        return this.promise;
    }

    public getProductByUrlKey(urlKey: string): Observable<JsonProduct> {
        return this.getProducts().pipe(
            map((data: JsonProduct) => {
                const products = Object.values(data);
                return products.find((prod) => prod.url_key === urlKey) || null;
            }),
        );
    }

    public getProductByUrl(urlKey: string): Observable<JsonProduct> {
        return this.oauth2Resource
            .useSalesApi()
            .ventes()
            .produits()
            .setUrl(`/${urlKey}`)
            .setParams({ attribute: 'url_key', parent: 'true' })
            .get();
    }

    public getProductsBySku(sku: string): Observable<JsonProduct> {
        return this.getProducts().pipe(
            map((products: JsonCatalog) => {
                for (const key in products) {
                    if (products[key].sku === sku) {
                        return products[key];
                    }
                }
                throw new Error('SKU not found');
            }),
        );
    }

    public getProductsByGencode(gencode: string, cache: boolean = true): Observable<JsonProduct> {
        if (cache && this.products && this.products[gencode]) {
            return of(this.products[gencode]);
        }
        return this.oauth2Resource
            .useSalesApi()
            .ventes()
            .produits()
            .setUrl(`/${gencode}`)
            .get()
            .pipe(
                map((response) => {
                    this.products[gencode] = response;
                    return response;
                }),
            );
    }

    public getProductsByGencodeInReferentiel(gencode: string, cache: boolean = true): Observable<JsonProduct> {
        if (cache && this.products && this.products[gencode]) {
            return of(this.products[gencode]);
        }
        return this.oauth2Resource
            .useSalesApi()
            .referentiel()
            .produits()
            .setUrl(`/${gencode}`)
            .get()
            .pipe(
                map((response) => {
                    this.products[gencode] = response;
                    return response;
                }),
            );
    }

    public getProductsByListGencode(listGencode: string, withChildren?: string): Observable<JsonCatalog> {
        return this.oauth2Resource
            .useSalesApi()
            .ventes()
            .produits()
            .setNoCache(false)
            .setParams({ listGencode, withChildren: withChildren || 'true' })
            .get()
            .pipe(
                map((response) => {
                    this.products = { ...this.products, ...response.produits };
                    return response.produits;
                }),
            );
    }

    public getOptionsSouscriptibles(gencodePlan: string, listGencodeOption: string[] = []) {
        return this.oauth2Resource
            .referentiel()
            .offres()
            .optionsSouscriptibles()
            .post({
                parcours: 'ACTIVATION',
                univers: 'MOBILE',
                cibleClient: 'GP',
                offresPanier: {
                    idAbonnement: gencodePlan,
                    options: listGencodeOption,
                },
            })
            .pipe(map((response) => response.offresSecondaires.map((data) => data.idOption)));
    }

    public getOffresAdditionnellesSouscriptibles(
        context: QuoteContextRenewalModel,
        idOffrePrincipale: string,
        selectedOptions: string[] = null,
    ) {
        const params: BasicObject = {
            idOffrePrincipale,
            parcours: 'RENOUVELLEMENT',
        };

        if (selectedOptions && selectedOptions.length > 0) {
            params.idOffresAdditionnelles = selectedOptions.join(',');
        }

        return this.oauth2Resource
            .useSalesApi()
            .contracts(String(context.contractId))
            .offresAdditionnellesSouscriptibles()
            .setParams(params)
            .get()
            .pipe(map((response) => response.offresAdditionnelles.map((data) => data.idOffre)));
    }

    /**
     * Return product by attribute by type_id attribute
     */
    public getProductsByType(type: string): Observable<JsonCatalog> {
        return this.oauth2Resource.useSalesApi().ventes().produits().setParams({ type }).get();
    }

    public getProductWithPrices(
        products: string[],
        plan?: string,
        fundingMode: FUNDING_MODES = FUNDING_MODES.MIN_ONE_OFF,
        contractId?: string,
        isPro?: boolean,
    ) {
        const younitedCookie = this.cookieStorage.get('younitedCreditActif');
        let isStandAloneProduct = false;
        const params: any = {
            boutique: 'web',
            modePourFinancement: fundingMode,
            clientCategorie: isPro ? 'PRO' : 'GP',
            detail: 1,
        };

        if (plan) {
            params.gencodes = plan;
        } else {
            params.gencodes = products.join(',');
            isStandAloneProduct = true;
        }
        if (younitedCookie.length > 0) {
            params.younitedCreditActif = younitedCookie;
        }
        return this.oauth2Resource
            .murContextualise()
            .setParams(params)
            .post(this.createBodyProductContextualise(isStandAloneProduct ? [] : products, contractId));
    }

    public getProductsWallByType(type: string, priceParams: WallPricesParams): Observable<JsonCatalog> {
        const params = {
            type,
            typeParcours: 'ACQUISITION',
            ...priceParams,
        };
        return this.oauth2Resource.useSalesApi().ventes().murProduits().setParams(params).get();
    }

    public getPromotionFromApi(gencodes: string[] = [], plan: string[] = []): Observable<PromotionsModelWebContainers> {
        const gencodesPerPages: string[][] = gencodes.length
            ? gencodes.reduce((acc, cur, i) => {
                  const idx = Math.floor(i / 50);
                  const page = acc[idx] || (acc[idx] = []);
                  page.push(cur);
                  return acc;
              }, [])
            : [];

        return forkJoin(
            gencodesPerPages.map((productGencodes) =>
                this.oauth2Resource
                    .useSalesApi()
                    .ventes()
                    .panier()
                    .eligibilitePromotionnelle()
                    .post(this._getBodyPromoDto(productGencodes, plan))
                    .pipe(
                        catchError(() => of({})),
                        map((carts: IPromotionsCartV2[]) => {
                            const cartForAllProducts = [];
                            carts.forEach((cart) => {
                                if (!!cart.avecLeProduit) {
                                    cartForAllProducts.push(cart);
                                } else {
                                    cart.parcours[0].produits.forEach((produit) => {
                                        cartForAllProducts.push({ avecLeProduit: produit.gencode, ...cart });
                                    });
                                }
                            });
                            return cartForAllProducts;
                        }),
                        map((carts: IPromotionsCartV2[]) =>
                            carts.reduce(
                                (acc, cartPromotions) => ({
                                    ...acc,
                                    [cartPromotions.avecLeProduit]: this._getPromotionsOfApiCarts(cartPromotions),
                                }),
                                {},
                            ),
                        ),
                    ),
            ),
        ).pipe(
            catchError(() =>
                of([
                    gencodes.reduce(
                        (acc, gencode) => ({
                            ...acc,
                            [gencode]: gencodes.reduce(
                                (accBis, gencodeBis) => ({
                                    ...accBis,
                                    [gencodeBis]: new PromotionsModelWeb([], [], []),
                                }),
                                {},
                            ),
                        }),
                        {},
                    ),
                ]),
            ),
            map((promotionsProducts) =>
                promotionsProducts.reduce((acc, promotions) => ({ ...acc, ...promotions }), []),
            ),
        );
    }

    public getPromotionFromProduitsContextualises(
        gencodePhone: string[],
        categories: string,
        isPro: boolean = false,
        contractId?: string,
    ): Observable<ApiProductContainers> {
        const younitedCookie = this.cookieStorage.get('younitedCreditActif');
        const params: any = {
            boutique: 'web',
            modePourFinancement: FUNDING_MODES.MIN_ONE_OFF,
            clientCategorie: isPro ? 'PRO' : 'GP',
            categorie: categories,
            detail: 1,
            limite: 50,
        };
        if (younitedCookie.length > 0) {
            params.younitedCreditActif = younitedCookie;
        }
        return this.oauth2Resource
            .murContextualise()
            .setParams(params)
            .post(this.createBodyProductContextualise(gencodePhone, contractId))
            .pipe(
                map((products: ApiContextualiseResponse) =>
                    products.produits.reduce(
                        (acc, el) => ({
                            ...acc,
                            [el.gencode]: el,
                        }),
                        {},
                    ),
                ),
            );
    }

    public getPromotionsForPlansFromApi(
        phoneGencode: string[],
        plansGencodes: string[],
        isPro: boolean = false,
        contractId?: string,
        extraJourneys?: {
            hasMobileJourney?: boolean;
            hasFixJourney?: boolean;
        },
    ): Observable<ApiProductContainers> {
        const younitedCookie = this.cookieStorage.get('younitedCreditActif');
        const params: any = {
            boutique: 'web',
            modePourFinancement: FUNDING_MODES.MIN_ONE_OFF,
            clientCategorie: isPro ? 'PRO' : 'GP',
            gencodes: plansGencodes.join(','),
            detail: 1,
            limite: 50,
        };
        if (younitedCookie.length > 0) {
            params.younitedCreditActif = younitedCookie;
        }
        return this.oauth2Resource
            .murContextualise()
            .setParams(params)
            .post(this.createBodyProductContextualise(phoneGencode, contractId, extraJourneys))
            .pipe(
                map((products: ApiContextualiseResponse) =>
                    products.produits.reduce(
                        (acc, el) => ({
                            ...acc,
                            [el.gencode]: el,
                        }),
                        {},
                    ),
                ),
            );
    }

    public findPlanInPanierSimule(panierSimuleProduits: ApiProduct[]): any {
        return panierSimuleProduits?.filter(
            (product) => product.type.includes('plan') || product.type.includes('faim'),
        )[0];
    }

    public getApiProduct(panierSimuleProduits: ApiProduct[]): ApiProduct {
        return panierSimuleProduits?.filter(
            (product) =>
                product.type.includes('phone') || product.type.includes('accessory') || product.type.includes('box'),
        )[0];
    }

    /**
     * Return products by category_code attribute
     */
    public getProductsByCategories(
        categoryCodes: string[],
        lightProducts: boolean = false,
    ): Observable<PlanJsonCategories> {
        let categoryStrings: string = '';
        categoryCodes.forEach((category: string) => {
            category = categoryStrings.length ? `,${category}` : category;
            categoryStrings = categoryStrings.concat(category);
        });
        return this.oauth2Resource
            .useSalesApi()
            .ventes()
            .categories()
            .setNoCache(false)
            .setParams({ codes: categoryStrings, voirProduits: 'true', avecDetails: `${!lightProducts}` })
            .get()
            .pipe(map((response) => response));
    }

    public getProductsByCategoriesCode(categoryCodes: string[]): Observable<any> {
        let categoryStrings: string = '';
        categoryCodes.forEach((category: string) => {
            categoryStrings += categoryStrings.length ? `,${category}` : category;
        });
        return this.oauth2Resource.useSalesApi().ventes().produits().setParams({ categories: categoryStrings }).get();
    }

    /**
     * Return product by attribute by type_id attribute
     */
    public getProductsByTypes(types: string[]): Observable<JsonCatalog> {
        return this.oauth2Resource.useSalesApi().ventes().produits().setParams({ type: types.toString() }).get();
    }

    /**
     * catalog resolver
     * @param route [description]
     * @param    state [description]
     * @return           [description]
     */
    public resolve(): Observable<JsonCatalog> {
        return this.getProducts();
    }

    public getDeliveryModes(params: DeliveryModesParams): Observable<DeliveryModesResult> {
        return this.oauth2Resource.useSalesApi().ventes().panier().setNoCache(false).modesLivraison().post(params);
    }

    /**
     * @function handleError
     * @return any
     */
    protected handleError(error: HttpEvent<string>) {
        console.log('Une erreur technique :' + error);
        return observableThrowError('Server error!');
    }

    private _getPromotionsOfApiCart(param: IPromotionsCartV2Produit): PromotionsModelWeb {
        return [param].reduce(
            (acc, product) =>
                new PromotionsModelWeb(
                    this._mergePromotions(
                        acc.automatic,
                        product.promotions.automatiques.map((p) => this._createPromotionv2Model(p, [product.gencode])),
                    ),
                    this._mergePromotions(
                        acc.manual,
                        product.promotions.manuelles.map((p) => this._createPromotionv2Model(p, [product.gencode])),
                    ),
                    this._mergePromotions(
                        acc.incitation,
                        product.promotions.incitations.map((p) => this._createPromotionv2Model(p, [product.gencode])),
                    ),
                ),
            new PromotionsModelWeb([], [], []),
        );
    }

    private _getPromotionsOfApiCarts(cartPromotions: IPromotionsCartV2): PromotionsModelWebContainer {
        return cartPromotions.parcours[0].produits.reduce(
            (accBis, product) => ({
                ...accBis,
                [product.gencode]: this._getPromotionsOfApiCart(product),
            }),
            {},
        );
    }

    private _createPromotionv2Model(promotionData: IPromotionV2, products: string[]): PromotionModel {
        const data = {
            id: promotionData.id,
            addProduct: promotionData.produitAAjouter,
            name: promotionData.nom,
            label: promotionData.libelle,
            type: promotionData.type,
            description: promotionData.description,
            amount: +promotionData.valeurRemise,
            reductionType: promotionData.typeRemise === 'POURCENTAGE' ? REDUCTION_TYPE.POURCENT : REDUCTION_TYPE.FIX,
            duration: promotionData.duree,
            dates: promotionData.dates,
            code: promotionData.coupons?.length ? promotionData.coupons[0] : null,
            message: {
                ...promotionData.message,
                ...{ portability_other: promotionData.proprietesSupplementaires?.blocPorta },
            },
            relevantProducts: products,
            accessTypes: (promotionData?.typesAcces as ACCESS_TYPE[]) || [],
        };
        return new PromotionModel(data);
    }

    private _mergePromotions(promotionsA: PromotionModel[], promotionsB: PromotionModel[]): PromotionModel[] {
        for (const promoB of promotionsB) {
            const PromoA = promotionsA.find((p) => p.id === promoB.id);
            if (PromoA) {
                PromoA.relevantProducts = promoB.relevantProducts.concat(PromoA.relevantProducts);
            } else {
                promotionsA.push(promoB);
            }
        }

        return promotionsA;
    }

    private _getBodyPromoDto(productsGencodes: string[] = [], cartGencodes?: string[]): any {
        const idPU = this.oauth2.getPersonId();
        return {
            ...(!!idPU && { idPU }),
            typeClient: this.proService.getIsProClientType() ? 'PRO' : 'GP',
            panier: {
                promotionsManuelles: [],
                parcours: [
                    {
                        type: this.getClientJourney(),
                        idVirtuel: 0,
                        promotionsManuelles: [],
                        produits: cartGencodes.map((gencode, index) => ({
                            promotionsManuelles: [],
                            gencode,
                            idVirtuel: index,
                        })),
                    },
                ],
            },
            produits: productsGencodes.map((gencode) => ({
                gencode,
                ajouterDansLeParcours: 0,
            })),
        };
    }

    private getCartGencodes(planDefault?: string): string[] {
        const cartSchemeProducts = this.cartHelperService.getQuote()?.items || [];
        const result = cartSchemeProducts
            .filter(
                (cartProduct) =>
                    cartProduct.source.type !== 'edp' && (cartProduct.source.type !== 'plan' || !planDefault),
            )
            .map((cartProduct) => cartProduct.source.gencode);

        if (planDefault) {
            result.push(planDefault);
        }

        return result;
    }

    private getClientJourney(): string {
        return window.location.pathname.split('/').some((part) => part === 'renouvellement')
            ? 'RENOUVELLEMENT'
            : 'ACQUISITION';
    }

    private createBodyProductContextualise(
        products: string[],
        contractId?: string,
        extraJourneys?: {
            hasMobileJourney?: boolean;
            hasFixJourney?: boolean;
        },
    ): object {
        const typeParcours = contractId ? 'RENOUVELLEMENT' : 'ACQUISITION';
        const faimCardQuotes = this.cartHelperService.getCheckoutFaimQuote();
        const currentProducts = [];
        if (products.length > 0) {
            currentProducts.push(this.createPanierElement(products[0]));
        }

        const currentParcours = [
            {
                estCourant: true,
                type: typeParcours,
                produits: currentProducts,
            },
        ];

        if (contractId) {
            currentParcours[0]['idContrat'] = contractId;
        }
        if (faimCardQuotes?.products?.length > 0) {
            const faimProducts = [];
            faimCardQuotes.products.map((quote) => faimProducts.push(this.createPanierElement(quote?.gencode)));
            currentParcours.push({
                estCourant: false,
                type: QUOTE_CONTEXTS.ACQUISITIONFIX,
                produits: faimProducts,
            });
        }
        const defaultFaiSimulatorGencode = this.configService.getData()?.global.defaultSimulatorGencode.fai;
        const defaultMobileSimulatorGencode = this.configService.getData()?.global.defaultSimulatorGencode.mobile;
        const { hasMobileJourney, hasFixJourney } = extraJourneys || {};
        if (hasFixJourney && defaultFaiSimulatorGencode) {
            currentParcours.push({
                estCourant: false,
                type: QUOTE_CONTEXTS.ACQUISITIONFIX,
                produits: [this.createPanierElement(defaultFaiSimulatorGencode)],
            });
        }
        if (hasMobileJourney && defaultMobileSimulatorGencode) {
            currentParcours.push({
                estCourant: false,
                type: QUOTE_CONTEXTS.ACQUISITION,
                produits: [this.createPanierElement(defaultMobileSimulatorGencode)],
            });
        }
        return {
            panier: {
                coupons: [],
                parcours: currentParcours,
            },
        };
    }

    private createPanierElement(gencode) {
        return {
            gencode,
        };
    }
}
